import { acceptHMRUpdate, defineStore } from "pinia";
import { computed } from "vue";
import { RemovableRef, useLocalStorage } from "@vueuse/core";

import type { AuthenticationResult, JsonWebToken } from "@/types/authentication";
import type { TenantContextPayload } from "@/types/api/fetch";
import { normalizeDateTime, utcNow } from "@/helpers/dateTimeUtilities";
import {
    buildStandardHeaders,
    defaultReasonForAbortingApiCall,
    goFetch,
    isOkStatus,
    isSuccessState,
    normalizeFetchParameters
} from "@/utils/network-utils";

import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
import { AuthenticationFailed, NotFound } from "@/types/errors";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

function assignToken( token: string | JsonWebToken ): void {
    logger.debug( `[ auth-store ].assignToken( "${ token }" ) called` );

    let tempHolder: JsonWebToken | undefined;
    if ( typeof token === 'string' ) {
        // Store the value of the shared JWT token
        tempHolder = {
            jwt: token,
            sessionId: undefined,
            timeToLive: undefined,
            altToken: undefined
        };
    } else {
        // Store the value of the shared JWT token
        tempHolder = token;
    }

    lsToken.value = JSON.stringify( tempHolder );
}

function parseResponse( payload: AuthenticationResult, altAuthToken?: string | undefined ): JsonWebToken | undefined {
    let result: JsonWebToken | undefined = undefined;
    const authResult = payload?.authResult;
    if ( authResult ) {
        const tokenValue = authResult.token ?? undefined;
        logger.debug( `Received token: ${ tokenValue }` );

        // track the session ID for the JsonWebToken
        const sessionId = authResult.sessionId ?? undefined;
        logger.debug( `Received session ID: ${ sessionId }` );

        // track the TTL for the JsonWebToken
        const ttl = normalizeDateTime( authResult.validTo ?? undefined );
        logger.debug( `Received ttl: ${ ttl }` );

        // track the alternative (laravel) token for the JsonWebToken
        const altToken: string | undefined = authResult.alternativeTokens?.length > 0 ? authResult.alternativeTokens[ 0 ].token : undefined;

        // create the JsonWebToken container from the data available from the Flagship authentication response
        result = {
            jwt: tokenValue,
            sessionId: sessionId,
            timeToLive: ttl,
            altToken: altToken ?? altAuthToken
        };
    }

    return result;
}

function reset() {
    lsToken.value = null;
}

/**
 * Verify that the session id is still valid on the server
 * @returns - expected response is 200 (valid), 204 (not valid), 401 (unauthorized) or 403 (forbidden)
 */
export async function checkForValidSession( authToken: string | null ): Promise< boolean > {
    let isSessionStillValid = false;

    // If no JWT is provided, it is assumed the user is not logged in & thus the session is no longer valid
    if ( !authToken ) {
        return isSessionStillValid;
    }

    const fetchParams = normalizeFetchParameters( {
        endpoint: 'authentication/sessionsummarybyjwt',
        payload: authToken
    } );

    const headers = buildStandardHeaders( authToken );

    // Create an AbortController to short-circuit an API call that is taking too long
    const monitor = new AbortController();
    const timeoutId = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), fetchParams.maxApiDuration );

    try {
        const sessionResponse: Response =
            await fetch( fetchParams.uri,  {
                method: fetchParams.method,
                body: fetchParams.body,
                headers: headers,
                signal: monitor.signal
            } );

        // Clear the timout at the earliest point to avoid false negative results
        clearTimeout( timeoutId );

        if ( isOkStatus( sessionResponse?.status ) ) {
            isSessionStillValid = true;
        }
    } catch ( err: any ) {
        // Clear the timout at the earliest point to avoid false negative results
        clearTimeout( timeoutId );

        isSessionStillValid = false;
    }

    return isSessionStillValid;
}

const TOKEN_LOCALSTORAGE_KEY: string = 'token';

// Assigned in the "assignToken" action
const lsToken: RemovableRef< string | null > = useLocalStorage( TOKEN_LOCALSTORAGE_KEY,null );

const token = computed< JsonWebToken | null >( () => {
    logger.debug( `TokenManager.getToken(): this._token[ ${ JSON.stringify( lsToken.value ) } ], this._tokenKeyName[ ${ TOKEN_LOCALSTORAGE_KEY } ]` );
    const localToken = !!( lsToken.value ) ? JSON.parse( lsToken.value ) : null;

    if ( localToken ) {
        const ttlExists = !!( localToken!.timeToLive );
        if ( ttlExists ) {
            const ttlIsValid = normalizeDateTime( localToken!.timeToLive ).isValid;
            if ( ttlIsValid ) {
                const isStillAlive = utcNow() < normalizeDateTime( localToken!.timeToLive );
                if ( isStillAlive ) {
                    return localToken;
                }
            }
        }
    }

    // Fall out by resetting the token storage and returning null
    reset();
    return null;
} );

const altAuthToken = computed< string | null >( () => token.value?.altToken ?? null );
const authToken = computed< string | null >( () => token.value?.jwt ?? null );
const sessionId = computed< string | null >( () => token.value?.sessionId ?? null );

export const useAuthStore = defineStore( 'authentication', () => {
    const isAuthenticated = computed< boolean >( () => !!( token.value?.jwt ) );

    async function authenticate( credentials: any ) {
        const username: string = credentials?.username ?? undefined;
        const password: string = credentials?.password ?? undefined;

        logger.debug( `[AuthHandler].authenticate( ${ username }, ${ password } )` );

        if ( !( username && password ) ) {
            throw new Error( `The username & password are required: ${ JSON.stringify( credentials ) }` );
        }

        const response =
            await goFetch< AuthenticationResult, TenantContextPayload >( {
                endpoint: `authentication/initiateauth`,
                payload: {
                    secrets: [
                        { key: "username", value: username },
                        { key: "userpassword", value: password }
                    ]
                }
            } ).catch( ( err: any ) => {
                throw new AuthenticationFailed( err.message )
            } );

        if ( response?.data && isSuccessState( response.status ) ) {
            const authToken: JsonWebToken | undefined = parseResponse( response.data );
            if ( !!( authToken ) ) {
                assignToken( authToken! );
            }
        } else {
            throw new NotFound();
        }
    }

    async function authenticateAsUser( userId: number, altAuthToken: string ): Promise< boolean > {
        // Using the token associated with a Laravel session, finish the authentication process
        const authResponse =
            await goFetch< AuthenticationResult, TenantContextPayload >( {
                endpoint: `authentication/initiateauth`,
                payload: {
                    identityProviderId: "93765f84-5413-4b72-9629-a4ef98d867e9",
                    applicationId: "bc8a907b-0e60-49a2-96ba-43a9b227c1d0",
                    tenantId: "926ab952-dd32-4791-afe5-7f9b17388118",
                    secrets: [
                        { key: 'userid', value: `${ userId }` },
                        { key: "token", value: altAuthToken },
                        { key: "tokenname", value: "log-in-as-a-user" }
                    ]
                }
            } );

        let authenticationResult: boolean = false;
        if ( authResponse?.data && isSuccessState( authResponse.status ) ) {
            const authToken: JsonWebToken | undefined = parseResponse( authResponse.data, altAuthToken );

            authenticationResult = !!( authToken );
            if ( authenticationResult ) {
                assignToken( authToken! );
            }
        } else {
            logger.error( `An invalid response was received when attempting to log in to the authentication server.` );
        }

        return authenticationResult;
    }

    /**
     * Clean up any current session information held on the server in a consistent manner and then
     * remove all artifacts of being logged in from the current browser (localstorage)
     */
    async function logout() {
        // regardless of the above result, clear out the current token from localstorage to avoid odd "auth" issues
        reset();
    }

    return {
        // ACTIONS
        authenticate,
        authenticateAsUser,
        logout,

        // STATE
        altAuthToken,
        authToken,
        isAuthenticated,
        sessionId
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useAuthStore, import.meta.hot ) );
}
